import { Box, Flex, HStack, IconButton, Link, Text } from "@chakra-ui/react";
import { RiArrowRightLine } from "@remixicon/react";
import { createContext, ReactNode, useContext, useState } from "react";
import { Link as RouterLink } from "react-router";

import { IcoClose, IcoEnvironmentDot } from "@/common/components/Icons";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useEnvironmentColors } from "@/environment/hooks/useEnvironmentColors";
import { EnvironmentUrl } from "@reflag/shared/urls";

export const TOP_BANNER_HEIGHT = 8;

// Banner context
interface BannerContextValue {
  isDemoBannerDismissed: boolean;
  dismissDemoBanner: () => void;
}

const BannerContext = createContext<BannerContextValue | null>(null);

export function BannerProvider({ children }: { children: ReactNode }) {
  const [isDemoBannerDismissed, setIsDemoBannerDismissed] = useState(false);

  const dismissDemoBanner = () => {
    setIsDemoBannerDismissed(true);
  };

  return (
    <BannerContext.Provider
      value={{
        isDemoBannerDismissed,
        dismissDemoBanner,
      }}
    >
      {children}
    </BannerContext.Provider>
  );
}

function useBannerContext() {
  const context = useContext(BannerContext);
  if (!context) {
    throw new Error("useBannerContext must be used within a BannerProvider");
  }
  return context;
}

interface BannerConfig {
  message: ReactNode | string;
  actionText: string;
  targetUrl: string;
  bgLightColor: string;
  bgDarkColor: string;
  isDismissable: boolean;
}

export const useTopBanner = () => {
  const { org, app, env } = useEnvironment();
  const { isDemoBannerDismissed, dismissDemoBanner } = useBannerContext();

  const nonDemoApp = org.apps.find((app) => !app.demo);
  const nonDemoEnv = nonDemoApp?.environments.find((env) => env.isProduction);
  const prodEnv = app.environments.find((env) => env.isProduction);

  const colors = useEnvironmentColors();

  let bannerConfig: BannerConfig | null = null;

  // Check for demo banner first, but only show if not dismissed
  if (app.demo && nonDemoEnv && !isDemoBannerDismissed) {
    bannerConfig = {
      message: "Demo app",
      actionText: "Switch to live app",
      targetUrl: EnvironmentUrl(nonDemoEnv),
      bgLightColor: "#96B8C5",
      bgDarkColor: "#8CB2C0",
      isDismissable: true,
    };
  }
  // Check for non-production banner after
  else if (!app.demo && !env.isProduction && prodEnv) {
    bannerConfig = {
      message: (
        <HStack spacing={1}>
          <IcoEnvironmentDot color={colors.nonProduction.icon} size="xs" />
          <Text>{env.name} environment</Text>
        </HStack>
      ),
      actionText: "Switch to production",
      targetUrl: EnvironmentUrl(prodEnv),
      bgLightColor: "#F1DAC1",
      bgDarkColor: "#EACCA9",
      isDismissable: false,
    };
  }

  // Only show banner if we have a valid config
  const showBanner = bannerConfig !== null;

  return {
    showBanner,
    bannerConfig,
    dismissDemoBanner,
  };
};

export function TopBanner() {
  const { showBanner, bannerConfig, dismissDemoBanner } = useTopBanner();

  if (!showBanner || !bannerConfig) return null;

  const handleDismiss = () => {
    dismissDemoBanner();
  };

  return (
    <Box
      h={TOP_BANNER_HEIGHT}
      left={0}
      pos="sticky"
      right={0}
      top={0}
      zIndex={3}
    >
      <Flex
        bgImage={`linear-gradient(180deg, ${bannerConfig.bgLightColor} 90%, ${bannerConfig.bgDarkColor} 100%)`}
        flexDirection="row"
        fontSize="sm"
        gap={4}
        h="full"
        justify="center"
        position="relative"
      >
        <Link
          _hover={{ textDecoration: "none" }}
          alignItems="center"
          as={RouterLink}
          display="flex"
          flex="1"
          justifyContent="center"
          textDecoration="none"
          to={bannerConfig.targetUrl}
        >
          <Flex
            align="center"
            color="gray.950"
            direction="row"
            fontWeight="semibold"
            textDecoration="none"
          >
            {bannerConfig.message}
          </Flex>
          <Flex
            align="center"
            color="blackAlpha.600"
            direction="row"
            gap={1}
            justify="space-between"
            ml={4}
          >
            <Text fontWeight="medium">{bannerConfig.actionText}</Text>
            <RiArrowRightLine size={14} />
          </Flex>
        </Link>

        {/* Only show dismiss button for dismissable banners */}
        {bannerConfig.isDismissable && (
          <IconButton
            _hover={{
              backgroundColor: "blackAlpha.200",
            }}
            aria-label="Hide"
            color="blackAlpha.600"
            icon={<IcoClose size="md" />}
            position="absolute"
            right={1}
            size="2xs"
            top={1}
            variant="ghost"
            onClick={handleDismiss}
          />
        )}
      </Flex>
      <Corner bgDarkColor={bannerConfig.bgDarkColor} placement="top-left" />
      <Corner bgDarkColor={bannerConfig.bgDarkColor} placement="top-right" />
    </Box>
  );
}

function Corner({
  placement,
  bgDarkColor,
}: {
  placement: "top-left" | "top-right";
  bgDarkColor: string;
}) {
  return (
    <Box
      height="10px"
      left={placement === "top-left" ? 0 : undefined}
      pointerEvents="none"
      position="absolute"
      right={placement === "top-right" ? 0 : undefined}
      width="10px"
      zIndex={1}
    >
      <svg height="10" viewBox="0 0 10 10" width="10">
        <defs>
          <mask id={`tr-corner-mask-${placement}`}>
            <rect fill="white" height="10" width="10" />
            {placement === "top-right" ? (
              <path d="M0,0 A10,10 0 0,1 10,10 L0,10 Z" fill="black" />
            ) : (
              <path d="M10,0 A10,10 0 0,0 0,10 L10,10 Z" fill="black" />
            )}
          </mask>
        </defs>
        <rect
          fill={bgDarkColor}
          height="10"
          mask={`url(#tr-corner-mask-${placement})`}
          width="10"
        />
      </svg>
    </Box>
  );
}
